import { useEffect, useMemo, useRef, useState } from 'react';
import ViewControls from '../scene/ViewControls';
import { useTight } from '../../components/useTight';
import { useNavigate } from 'react-router-dom';
import RowMenu from '../../components/RowMenu';
import { KINDS } from '../../concepts/kinds';
import { isKnownKind, type ProgramTrack, type ProgramView } from '../../programs/types';
import { AddProgramsMenu } from '../../pages/home/shared';
import { ProgramMenuItems } from '../../pages/home/ClassicHome';
import type { HomeModel } from '../../pages/home/useHome';
import { colourOfCategory, glyphOf, growthText, motionAllowed, useGrowth } from '../scene/programs';
import { useFollow, useScene } from '../scene/useScene';
import type { GardenHandle, PlantSpec } from './Garden';

const FLOWERS = ['#f4719a', '#ff9f43', '#a77bf3', '#ff6b6b', '#f7d046', '#5ec2f2', '#ffffff'];

function flowerOf(id: string) {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return FLOWERS[h % FLOWERS.length];
}

function useWide() {
  const [wide, setWide] = useState(window.innerWidth > 720);
  useEffect(() => {
    const on = () => setWide(window.innerWidth > 720);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return wide;
}

export default function GardenHome({ h }: { h: HomeModel }) {
  const nav = useNavigate();
  const [track, setTrack] = useState<ProgramTrack>('CURRENT');
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);

  const insetsRef = useRef<() => void>(() => {});
  useEffect(() => {
    const bar = barRef.current;
    const top = document.querySelector<HTMLElement>('.topbar');
    const root = document.documentElement;
    if (!bar) return;
    const apply = () => {
      const b = bar.getBoundingClientRect();
      root.style.setProperty('--gdn-bar-h', `${Math.round(b.height)}px`);
      const docked = b.top > innerHeight / 2;
      const covered = Math.max(top?.getBoundingClientRect().bottom ?? 0, docked ? 0 : b.bottom);
      handleRef.current?.setInsets(covered, docked ? innerHeight - b.top : 0);
    };
    insetsRef.current = apply;
    const ro = new ResizeObserver(apply);
    ro.observe(bar);
    if (top) ro.observe(top);
    window.addEventListener('resize', apply);
    return () => { ro.disconnect(); window.removeEventListener('resize', apply); root.style.removeProperty('--gdn-bar-h'); };
  }, []);
  const wide = useWide();
  const crowded = useTight(barRef);
  const compact = crowded || !wide;
  const moving = motionAllowed(h.settings.reduceMotion);
  const list = track === 'CURRENT' ? h.current : h.maintenance;
  const growth = useGrowth(list);

  const plants: PlantSpec[] = useMemo(() => list.map((p) => ({
    id: p.id,
    bed: p.category ?? 'Other',
    colour: colourOfCategory(p.category),
    flower: flowerOf(p.id),
    stage: growth.get(p.id)?.stage ?? (p.track === 'MAINTENANCE' ? 4 : 0),
  })), [list, growth]);

  const { canvasRef, handleRef, failed } = useScene<GardenHandle>(
    'garden',
    (canvas, cb) => import('./Garden').then(({ mountGarden }) => mountGarden(canvas, moving, cb)),
    { onPick: (id) => setSelected(id), onHover: setHovered },
    (handle) => { handle.setPlants(plants); handle.setSelected(selected); insetsRef.current(); },
  );

  useEffect(() => { handleRef.current?.setPlants(plants); }, [plants]);
  useEffect(() => { handleRef.current?.setSelected(selected); }, [selected]);
  useEffect(() => { handleRef.current?.setMoving(moving); }, [moving]);
  useEffect(() => { setSelected(null); }, [track, h.categoryFilter, h.kindFilter]);

  const cardRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  useFollow(cardRef, () => (selected ? handleRef.current?.locate(selected) ?? null : null), wide && !!selected && !failed);
  useFollow(tipRef, () => (hovered ? handleRef.current?.locate(hovered) ?? null : null), !!hovered && hovered !== selected && !failed);

  const chosen = list.find((p) => p.id === selected) ?? null;
  const tip = list.find((p) => p.id === hovered) ?? null;
  const playUrl = (p: ProgramView) => `/play/${p.id}${h.learnerId ? `?learner=${encodeURIComponent(h.learnerId)}` : ''}`;
  const kindLabel = (p: ProgramView) => (isKnownKind(p.kind) ? KINDS[p.kind].label : p.kind);

  return (
    <div className="gdn">
      {!failed && <canvas ref={canvasRef} className="scene-canvas" aria-hidden="true" />}
      {!failed && <ViewControls handleRef={handleRef} />}

      <div className={compact ? 'gdn-bar is-tight' : 'gdn-bar'} ref={barRef}>
        <div className="gdn-tabs" role="tablist" aria-label="Which programs">
          {(['CURRENT', 'MAINTENANCE'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={track === t}
              className={track === t ? 'gdn-tab is-active' : 'gdn-tab'}
              onClick={() => setTrack(t)}
            >
              {t === 'CURRENT' ? 'Growing' : 'Learned'}
              <span className="gdn-count">{t === 'CURRENT' ? h.current.length : h.maintenance.length}</span>
            </button>
          ))}
        </div>
        <button type="button" className="gdn-pill gdn-options-toggle" aria-expanded={optionsOpen} aria-controls="gdn-options" aria-label="More options" onClick={() => setOptionsOpen((o) => !o)}>
          More <span aria-hidden="true">{optionsOpen ? '▴' : '▾'}</span>
        </button>
        <div id="gdn-options" className={optionsOpen ? 'gdn-options is-open' : 'gdn-options'}>
        {h.categories.length > 0 && (
          <select className="gdn-select" aria-label="Category" value={h.categoryFilter} onChange={(e) => h.setCategoryFilter(e.target.value)}>
            <option value="">All beds</option>
            {h.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
          {h.kinds.length > 1 && (
            <select className="gdn-select" aria-label="Type of game" value={h.kindFilter} onChange={(e) => { h.setKindFilter(e.target.value); }}>
              <option value="">All types of game</option>
              {h.kinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          )}
        <button type="button" className="gdn-pill" aria-expanded={listOpen} onClick={() => setListOpen((o) => !o)}>
          {listOpen ? 'Hide list' : 'Show list'}
        </button>
        </div>
        <div className="gdn-bar-end">
          <AddProgramsMenu h={h} text={wide ? 'Add programs' : '+'} />
        </div>
      </div>

      {h.error && <div className="error gdn-error" role="alert">{h.error}</div>}

      {!h.loading && list.length === 0 && (
        <div className="gdn-empty">
          <h2>{track === 'CURRENT' ? 'Nothing planted yet' : 'Nothing has flowered yet'}</h2>
          <p>{track === 'CURRENT' ? 'Add a program and it is planted here. It grows as the learner gets it right.' : 'Learned programs bloom here.'}</p>
        </div>
      )}

      {failed && list.length > 0 && !listOpen && (
        <p className="gdn-empty">The garden could not be drawn on this device. Open the list to see every program.</p>
      )}

      {listOpen && (
        <aside className="gdn-list" aria-label="Every program">
          <ul>
            {list.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className={selected === p.id ? 'gdn-item is-active' : 'gdn-item'}
                  aria-pressed={selected === p.id}
                  onClick={() => { setSelected(p.id); if (!wide) setListOpen(false); }}
                >
                  <span className="gdn-item-glyph" aria-hidden="true">{glyphOf(p)}</span>
                  <span className="gdn-item-text">
                    <span className="gdn-item-name">{p.name}</span>
                    <span className="gdn-item-meta">{growthText(growth.get(p.id))}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>
      )}

      {tip && tip.id !== selected && (
        <div ref={tipRef} className="gdn-tip" aria-hidden="true"><span>{tip.name}</span></div>
      )}

      {chosen && (
        <div ref={cardRef} className={wide ? 'gdn-card is-floating' : 'gdn-card'} role="region" aria-label={chosen.name}>
          <div className="gdn-card-inner">
            <div className="gdn-card-head">
              <span className="gdn-card-glyph" aria-hidden="true">{glyphOf(chosen)}</span>
              <div>
                <p className="gdn-card-name">{chosen.name}</p>
                <p className="gdn-card-meta">{chosen.category ?? kindLabel(chosen)} · {kindLabel(chosen)}</p>
              </div>
            </div>
            <div className="gdn-meter" aria-hidden="true">
              {[1, 2, 3, 4].map((n) => (
                <span key={n} className={(growth.get(chosen.id)?.stage ?? 0) >= n ? 'is-on' : ''} />
              ))}
            </div>
            <p className="gdn-card-growth">
              {growthText(growth.get(chosen.id))}
              {h.dueIds.has(chosen.id) ? ' · ready for a check' : ''}
            </p>
            <div className="gdn-card-actions">
              <button type="button" className="btn" onClick={() => nav(playUrl(chosen))}>Play</button>
              <RowMenu label={`More for ${chosen.name}`}>
                <ProgramMenuItems h={h} p={chosen} />
              </RowMenu>
              <button type="button" className="btn secondary" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
